// keyrd_console/src/components/PilotParticipants.js
import React, { useEffect, useMemo, useState } from "react";
import { getPilotParticipants } from "../api/pilotAdmin";
import PilotGovernanceBanner from "./PilotGovernanceBanner";
import PilotStatusPill from "./PilotStatusPill";
import PilotDataSourceBadge from "./PilotDataSourceBadge";

const pageStyle = {
  padding: 20,
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const titleRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 12,
};

const titleStyle = {
  fontSize: 24,
  fontWeight: 700,
  color: "#0f172a",
  margin: 0,
};

const subtitleStyle = {
  color: "#475569",
  fontSize: 14,
  marginTop: 4,
};

const controlRowStyle = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
};

const inputStyle = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  minWidth: 260,
};

const selectStyle = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
};

const tableWrapStyle = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  overflow: "hidden",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
};

const thStyle = {
  textAlign: "left",
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "#475569",
  background: "#f8fafc",
  borderBottom: "1px solid #e2e8f0",
  padding: "12px 10px",
  verticalAlign: "top",
};

const tdStyle = {
  padding: "12px 10px",
  borderBottom: "1px solid #e2e8f0",
  verticalAlign: "top",
  fontSize: 14,
  color: "#0f172a",
};

const clickableRowStyle = (selected) => ({
  cursor: "pointer",
  background: selected ? "#f0fdf4" : "#ffffff",
});

const metaTextStyle = {
  fontSize: 12,
  color: "#64748b",
};

const loadingStyle = {
  padding: 18,
  borderRadius: 10,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#334155",
};

const errorStyle = {
  padding: 14,
  borderRadius: 10,
  background: "#fef2f2",
  color: "#991b1b",
  border: "1px solid #fecaca",
};

const emptyStyle = {
  padding: 18,
  borderRadius: 10,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#475569",
};

function normalizeRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.participants)) return payload.participants;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
}

function matchesQuery(row, query) {
  if (!query) return true;
  const haystack = [
    row.participant_uuid,
    row.participant_label,
    row.invite_code,
    row.invite_status,
    row.cohort,
    row.lifecycle_state,
    row.operator_review_status,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(query.toLowerCase());
}

function filterRows(rows, query, statusFilter) {
  return rows.filter((row) => {
    const queryOk = matchesQuery(row, query);
    const filterOk =
      statusFilter === "all" ||
      String(row.lifecycle_state || "").toLowerCase() === statusFilter.toLowerCase() ||
      String(row.operator_review_status || "").toLowerCase() === statusFilter.toLowerCase();

    return queryOk && filterOk;
  });
}

function PilotParticipants({
  auth,
  selectedParticipantId,
  onSelectParticipant,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const res = await getPilotParticipants(auth);
        if (!cancelled) {
          setPayload(res?.data || res || {});
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load pilot participants.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  const data = payload || {};
  const rows = normalizeRows(data);
  const filteredRows = useMemo(
    () => filterRows(rows, query, statusFilter),
    [rows, query, statusFilter]
  );

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={loadingStyle}>Loading pilot participants…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={errorStyle}>{error}</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={titleRowStyle}>
        <div>
          <h1 style={titleStyle}>Participants</h1>
          <div style={subtitleStyle}>
            Full pilot roster with lifecycle, review, suppression, cooldown, and alert visibility.
          </div>
        </div>
        <PilotDataSourceBadge source={data.data_source} stub={Boolean(data.stub)} />
      </div>

      <PilotGovernanceBanner auth={auth} system={data.system || data} />

      <div style={controlRowStyle}>
        <input
          style={inputStyle}
          type="text"
          value={query}
          placeholder="Search participant, invite, cohort, UUID…"
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          style={selectStyle}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All statuses</option>
          <option value="drafted">Drafted</option>
          <option value="consented">Consented</option>
          <option value="baseline-ready">Baseline-ready</option>
          <option value="device-ready">Device-ready</option>
          <option value="shadow-active">Shadow-Active</option>
          <option value="review_pending">Review Pending</option>
          <option value="hold">Hold</option>
          <option value="paused">Paused</option>
        </select>
      </div>

      {!filteredRows.length ? (
        <div style={emptyStyle}>No participants matched the current filters.</div>
      ) : (
        <div style={tableWrapStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Participant</th>
                <th style={thStyle}>Invite / Cohort</th>
                <th style={thStyle}>Lifecycle</th>
                <th style={thStyle}>Readiness</th>
                <th style={thStyle}>Operational State</th>
                <th style={thStyle}>Alerts / Updated</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => {
                const selected = selectedParticipantId === row.participant_uuid;

                return (
                  <tr
                    key={row.participant_uuid}
                    style={clickableRowStyle(selected)}
                    onClick={() =>
                      typeof onSelectParticipant === "function" &&
                      onSelectParticipant(row.participant_uuid, "pilotParticipantDetail")
                    }
                  >
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 700 }}>{row.participant_label || "Unnamed Participant"}</div>
                      <div style={metaTextStyle}>{row.participant_uuid || "No UUID"}</div>
                    </td>

                    <td style={tdStyle}>
                      <div>{row.invite_code || "—"}</div>
                      <div style={metaTextStyle}>
                        {row.invite_status || "invite unknown"} · {row.cohort || "no cohort"}
                      </div>
                    </td>

                    <td style={tdStyle}>
                      <PilotStatusPill status={row.lifecycle_state || "unknown"} />
                      <div style={{ height: 6 }} />
                      <PilotStatusPill
                        status={row.operator_review_status || "review_pending"}
                      />
                    </td>

                    <td style={tdStyle}>
                      <div><PilotStatusPill status={row.consent_status || "unknown"} label={`Consent: ${row.consent_status || "unknown"}`} /></div>
                      <div style={{ height: 6 }} />
                      <div><PilotStatusPill status={row.baseline_status || "unknown"} label={`Baseline: ${row.baseline_status || "unknown"}`} /></div>
                      <div style={{ height: 6 }} />
                      <div><PilotStatusPill status={row.device_status || "unknown"} label={`Device: ${row.device_status || "unknown"}`} /></div>
                    </td>

                    <td style={tdStyle}>
                      <div><PilotStatusPill status={row.suppression_status || "unknown"} label={`Suppression: ${row.suppression_status || "unknown"}`} /></div>
                      <div style={{ height: 6 }} />
                      <div><PilotStatusPill status={row.cooldown_status || "unknown"} label={`Cooldown: ${row.cooldown_status || "unknown"}`} /></div>
                      <div style={{ height: 6 }} />
                      <div><PilotStatusPill status={row.pause_status || "unknown"} label={`Pause: ${row.pause_status || "unknown"}`} /></div>
                    </td>

                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600 }}>
                        Alerts: {row.alert_count ?? 0}
                      </div>
                      <div style={metaTextStyle}>
                        Last shadow: {row.last_shadow_decision_at_utc || "n/a"}
                      </div>
                      <div style={metaTextStyle}>
                        Updated: {row.last_updated_utc || "unknown"}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PilotParticipants;